import { useGiftGuideHubLink } from '../hub/giftGuideHubFlag';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';

import { shimmerFill } from '../../motion';
import {
  PAGE_GUTTER_DESKTOP,
  PAGE_GUTTER_DESKTOP_NEGATIVE,
  trackCardWidthCss,
} from '../lib/pageGutter';
import {
  buildCalendarDeepLink,
  holidayCalendarEventId,
  personCalendarEventId,
} from '../calendar/calendarDeepLink';
import { holidayCountdownLabel } from '../calendar/holidayCountdown';
import { destinationKind, eventDestination } from '../calendar/mockCalendarData';
import type { PersonEventType } from '../calendar/mockCalendarData';
import { useMaterializedComingUp } from '../hooks/useMaterializedComingUp';
import { useHiddenHolidays } from '../holidayPrefs/useHiddenHolidays';
import { GA_HOLIDAY_EVENT_TYPE, gaCalendarEventTap } from '../lib/gaPixel';
import { resolveRecipientEmoji } from '../lib/resolveRecipientEmoji';
import { useIsStaticRender } from '../lib/staticRenderContext';
import type { ComingUpEvent } from '../schemas/materializedComingUp';
import {
  eventDayIso,
  HOLIDAYS_RELEASED_IN_COMING_UP,
  holidayEntriesForComingUp,
  mergeComingUpEntries,
} from './comingUpEntries';
import type { ComingUpEntry, ComingUpPersonEntry } from './comingUpEntries';

/**
 * Coming Up strip — horizontal scroll of the next ~60 days of recipient
 * birthdays + anniversaries, rendered above the recipient grid on the
 * My People page.
 *
 * Reads from `useMaterializedComingUp` (single BE-materialized doc per
 * user). Zero client-side date math on the PERSON arm: the BE has already
 * sorted by `nextEventAt` ascending and denormalized recipientName +
 * recipientEmoji into each entry.
 *
 * Holidays merge in as a second arm (PR 2.4 / THE-760), chronologically
 * interleaved with the people — see `comingUpEntries.ts`, which owns the
 * holiday normalization, the saved hidden-list filter and the sort.
 * `HOLIDAYS_RELEASED_IN_COMING_UP` OPENED as of THE-764 (PR 3.3): production
 * renders now merge real holiday cards in, gated on the viewer's saved
 * `hiddenHolidays` list via `useHiddenHolidays()`'s `ready` flag below — NOT
 * on `hydrated` (the person arm), which is a different async read that
 * resolves on its own schedule.
 *
 * Defensive: skips entries with `nextEventAt < now()` (stale across the
 * event boundary until the nightly recompute fix from Phase 6b ships).
 * Caps the visible window to 60 days so the strip doesn't drift toward
 * "every event in the next year."
 */

// Visible look-ahead. The BE materializes 365 days; we display 60.
const LOOK_AHEAD_DAYS = 60;
const MS_PER_DAY = 86_400_000;

/**
 * The strip's own horizontal scroll offset, remembered across a mount
 * (THE-1403). A card tap now lands on the board/guide/collection itself
 * rather than on `/calendar`, so Back is a real unmount-then-remount of this
 * component, not a return to an already-mounted one — a plain `useRef`
 * cannot survive that. Module scope does, for as long as the SPA stays
 * loaded, which is exactly the lifetime a client-side Back needs.
 *
 * Keyed by account + HISTORY ENTRY (`location.key`), never global: Back
 * returns to the same entry and so restores, while any fresh arrival — the
 * People tab, a link from the Calendar, a new day, another account on this
 * browser — is a different key and starts at the first card.
 */
const rememberedScrollLeft = new Map<string, number>();

/** Test-only: forget every remembered offset so suites can't leak into each other. */
export function resetComingUpScrollMemory(): void {
  rememberedScrollLeft.clear();
}

/**
 * Router state on a holiday card that opens a guide, a collection or an
 * occasion page (THE-1403): the hub header then keeps People lit with a
 * "People" crumb back here (`resolveHubNavigation`), rather than reading the
 * page as a Gift guides visit — whose inferred crumb on a one-guide holiday
 * points at the very collection the tap skipped. The Calendar sends
 * `hubEntry: 'calendar'` for the same reason.
 */
const PEOPLE_ENTRY = { hubEntry: 'people' } as const;

// A layout effect in the browser (no first-card frame before a restore);
// a plain effect under the build's static prerender, where a layout effect
// only warns. Same split as `HubRowScroll`.
const useBeforePaint = typeof window === 'undefined' ? useEffect : useLayoutEffect;

// The full-bleed linen band (THE-746 Phase 2, PR 2.3). Replaces the
// inset blush-gradient banner: the band now runs edge to edge and the
// GUTTER MOVES INSIDE it (onto `Inner`), so the band breaks the page
// gutter while the content it holds keeps the same left-edge alignment
// as "Your people".
//
// Why `width: 100%` is genuinely full-bleed here rather than a 100vw
// escape hatch: every ancestor on the render path is unpadded and
// unconstrained — LandingPage's `Page` (no max-width), `Main`
// (display: contents, no box) and the `#your-people` wrapper div. The
// gutters on this page live inside each section, which is exactly why
// this component carried them. `100vw` would have been wrong anyway: it
// counts the desktop scrollbar and would add a horizontal scroll.
// INVARIANT: no max-width and no horizontal padding on this element —
// pinned by ComingUpStripStyleContract.test.ts.
//
// No `overflow` and no `border-radius`: both would re-clip the day-of
// confetti fountain that rises above the cards (see Scroller).
const Band = styled.section`
  width: 100%;
  background: ${({ theme }) => theme.color.linen};
  padding: 20px 0 16px;
  @media (min-width: 1024px) {
    padding: 28px 0 24px;
  }
`;

// The gutter the band itself no longer carries. Mirrors
// BrowseMyFriendsSection's Section so Coming Up and Your People share a
// left-edge alignment on every breakpoint (Kate feedback 2026-05-13: on
// desktop the strip was hugging the page edge while the tile grid sat
// ~64px inset — visually mismatched). Those numbers are unchanged below
// 1024px; only the element they sit on moved inward.
//
// NO MAX-WIDTH, NO MARGIN:AUTO (THE-1161) — ONLY WHEN `$wideGutter` IS SET,
// see hubType.ts's HUB_GUTTER_CSS doc comment for the identical gate
// (giftGuideHubFlag.ts, Kate 2026-10-27). A capped+centered Inner is
// exactly what left this strip cutting off 84px before the real edge at
// 1440px wide (64px padding + the cap's own 20px outer margin) and gave a
// 4-event strip no peek at all: Scroller (below) could bleed out of Inner's
// own padding, but never past a box that was independently capped at
// 1400px. Padding alone, growing via `PAGE_GUTTER_DESKTOP` past 1568px
// wide, reaches the real edge at every width — `false`/omitted reproduces
// the pre-PR cap+flat-64px box exactly.
const Inner = styled.div<{ $wideGutter?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ $wideGutter }) => !$wideGutter && 'max-width: 1400px; margin: 0 auto;'}
  padding: 0 16px;
  @media (min-width: 640px) {
    padding-left: 32px;
    padding-right: 32px;
  }
  @media (min-width: 1024px) {
    padding-left: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
    padding-right: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
  }
`;

// Section-header spec (v5): sentence case, left-aligned, one register —
// same 21px desktop / 19px mobile as "Your people". The old 15px all-caps
// muted eyebrow is retired (no all-caps eyebrows anywhere).
//
// Colour is `mochaText`, not --foreground: this text rides on `linen`,
// and the spec pairs those two (8.34:1, pinned by
// calendarTokenContrast.test.ts).
const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 19px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.mochaText};
  letter-spacing: -0.01em;
  line-height: 1.2;
  @media (min-width: 768px) {
    font-size: 21px;
  }
`;

// NO BLEED OF ITS OWN before THE-1161 — the track sat flush inside Inner's
// own padded box with nothing to escape it, so the LAST card's right edge
// could never go past Inner's own right edge. A 4-event strip (fixed
// 240px cards) filled only ~1000 of Inner's 1272px content width at 1440,
// which is what "no peek at all" meant: not a short peek, none. The
// negative-margin + matching padding pair below is the same technique
// every hub carousel track uses (HubFindsBand et al.) — box-sizing:
// border-box is what makes the padding this adds land inside, not on top
// of, the width the negative margin restores.
const Scroller = styled.div<{ $wideGutter?: boolean }>`
  /* Shepherd review (THE-1161): border-box only matters once the >=1024px
     bleed below adds horizontal padding — with no padding (the $wideGutter
     false/pre-PR case) content-box and border-box compute the same width,
     but gating it anyway keeps every declaration here honestly tied to the
     param, matching Kate's "no change when off" bar literally. */
  ${({ $wideGutter }) => $wideGutter && 'box-sizing: border-box;'}
  display: flex;
  gap: 10px;
  @media (min-width: 1024px) {
    /* THE-1161 shepherd QA round 4: bumped from 14px alongside Card's new
       clamp() width (see CARD_WIDTH_CSS) — the gap is part of the same
       (viewport minus gutter) mod (width plus gap) formula that decides
       where the resting unscrolled edge lands, so it has to move with the
       width formula, not stay fixed while that formula changes around it.
       $wideGutter-gated (giftGuideHubFlag.ts): pre-PR this was a flat
       14px, reasserted below when the flag is off. No nested css-tagged
       template here — a source fitness test
       (ComingUpStripStyleContract.test.ts) extracts this block by its
       first closing backtick, which a nested one would terminate early. */
    gap: ${({ $wideGutter }) => ($wideGutter ? '24px' : '14px')};
  }
  overflow-x: auto;
  /* THE-1161 shepherd QA: the person arm resolves a beat after the holiday
     arm, and when it does, an earlier-dated event (a recipient's own
     birthday) can land AHEAD of cards already on screen — "whatever's
     ready" render strategy above, by design. The browser's scroll anchor
     (CSS Scroll Anchoring) then holds the ALREADY-VISIBLE card's on-screen
     position by advancing scrollLeft to compensate for it moving one slot
     right, which scrolls the newly-prepended card mostly out of view
     instead of leaving scrollLeft at 0 to show it. Same fix CalendarPage's
     redesigned rail uses for the identical async-prepend class of bug.
     Shepherd review (THE-1161): gated behind $wideGutter — this fixes a
     latent bug independent of the gutter geometry, but shipping it to
     every off-param desktop visitor is a scope-creep behavior change
     Kate's "no change when off" bar rules out. Un-gate in a follow-up once
     the fix itself gets its own review. Moved inside the >=1024px block
     (shepherd round 13): the async-prepend race this guards against is a
     desktop-only report, and the bare top-level interpolation above applied
     it on phones too whenever $wideGutter was true, which the mobile-
     unchanged acceptance bar rules out just as much as the off-param case
     does. */
  /* Owns its horizontal axis -- THE-803 [PLAT]: every nested scroller
     declares overscroll-behavior on the axis it owns. The payoff is Android
     Chrome and laptop trackpads, where a flick running off the end chains
     into the document's horizontal axis and drives back/forward. NOT an iOS
     fix: measured on device 2026-10-05, this declaration makes no difference
     to iOS Safari's back gesture, which sits above the page out of CSS's
     reach. Latent today: THE-805 relaxed only the root's VERTICAL axis, so
     the root still carries overscroll-behavior-x: none as an outer belt and
     nothing chains here yet. This is the inner belt, gated so it cannot be
     dropped before it is the only one. Rationale and sweep:
     src/__tests__/railOverscrollContainment.test.ts. */
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  padding-bottom: 4px;
  /* Confetti headroom: overflow-x: auto forces overflow-y to auto too
     (CSS spec — visible can't pair with auto), so the scroll container
     CLIPS anything above the cards. The day-of confetti fountain rises
     ~90px above its card and was being clipped to invisible (the real
     "I didn't see it", 2026-07-10). padding-top + equal negative
     margin-top is net-zero layout but extends the clip box upward so
     the burst has sky. */
  padding-top: 96px;
  margin-top: -96px;
  /* Hide the scrollbar but keep the scroll affordance. */
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  /* THE-1161 shepherd QA correction: this bleed is a >=1024px-ONLY fix.
     Below 1024 the track sat flush inside Inner's 16/32px padding before
     this ticket, with no peek problem reported at those widths — the
     ticket's own acceptance bar says mobile is unchanged. Applying the
     same bleed+pad+scroll-pad at every breakpoint was net-zero for the
     first card's position (the padding cancels the margin) but moved the
     TRACK's own right edge out to the true screen edge at 390/768 too,
     which silently widened the scrollable range (and the last card's
     peek) exactly where the ticket said nothing should move. Gating this
     to >=1024px, where the no-peek bug was actually measured, is what
     keeps 390/768 byte-identical. $wideGutter-gated (giftGuideHubFlag.ts):
     pre-PR there was no bleed at all here, so the track sat flush inside
     Inner's own padding — reproduced below when the flag is off. */
  @media (min-width: 1024px) {
    ${({ $wideGutter }) => $wideGutter && 'overflow-anchor: none;'}
    ${({ $wideGutter }) =>
      $wideGutter &&
      'margin-left: ' +
        PAGE_GUTTER_DESKTOP_NEGATIVE +
        '; margin-right: ' +
        PAGE_GUTTER_DESKTOP_NEGATIVE +
        '; padding-left: ' +
        PAGE_GUTTER_DESKTOP +
        '; padding-right: ' +
        PAGE_GUTTER_DESKTOP +
        '; scroll-padding-left: ' +
        PAGE_GUTTER_DESKTOP +
        ';'}
  }
`;

// <Link>-based card so Cmd/Ctrl/Shift-click opens the recipient board in
// a new tab natively. Color: inherit + text-decoration: none cancel the
// default <a> styling.
//
// Card treatment (PR 2.3): white, borderless, ELEVATED — the cards float
// on the linen band instead of outlining against the page. Now 210px
// wide (was 168px) and laid out as a ROW: the date tile leads, the name /
// event / countdown stack to its right.
//
// The `$today` blush-gradient branch is GONE. Board answer #7 on THE-746
// replaced it with the deep-clay date tile, so the day-of treatment lives
// on the tile (plus the confetti), not on the card fill. Every card is
// plain white.
const CARD_SHADOW =
  '0 4px 14px rgba(120, 70, 50, 0.13), 0 1px 3px rgba(120, 70, 50, 0.08)';
const CARD_SHADOW_HOVER =
  '0 6px 18px rgba(120, 70, 50, 0.18), 0 2px 4px rgba(120, 70, 50, 0.10)';

// Mobile is the spec'd size; desktop is the one step up the old flat
// 168px-at-every-breakpoint card never had.
const CARD_WIDTH_PX = 210;
// THE-1161 round 8 (board, 2026-10-27): a flex-grow-to-fill behaviour used
// to live here (a sparse row spent spare room growing its cards edge to
// edge, a denser row held a separate fixed formula instead, switched by an
// entry-count threshold — see git history for the two retired mixins and
// the prop/constant that switched between them). The board's round-8 QA cut
// that branch entirely: every entry count now renders the SAME width,
// computed the SAME way Finds/Picks size
// their own cards (HubFindsBand.tsx / HubPicksBand.tsx both call
// `trackCardWidthCss(200, 4.3, 24)`) — "about 4 1/3 card-plus-gap slots fit
// in the track's visible width" — rather than this strip inventing its own
// divisor. A sparse row (2-3 events, or a real 4-event one at most widths)
// just sits flush at the left with linen showing after it, which is
// INTENTIONAL per the board ("a few events simply sit from the left edge,
// that's simpler and matches how calendars usually behave") rather than a
// gap this component has to paper over by stretching cards to fill it.
// Once there ARE enough events to overflow the row (`flex-shrink: 0` below
// is what keeps an overflowing row from being squeezed back down to fit),
// the resting edge lands inside a card — a real "there's more" peek — by
// the same `(viewport - gutter) mod (width + gap)` property
// `trackCardWidthCss`'s own doc comment explains, verified for this strip
// in the "desktop peek geometry" describe block in
// ComingUpStripStyleContract.test.ts.
// `$wideGutter` (giftGuideHubFlag.ts): pre-PR this was a flat 240px
// (CARD_WIDTH_DESKTOP_PX) at >=1024px — reasserted below when the flag is
// off, in place of the board's round-8 trackCardWidthCss formula.
const CARD_WIDTH_DESKTOP_PX = 240;
const CARD_WIDTH_CSS = css<{ $wideGutter?: boolean }>`
  width: ${CARD_WIDTH_PX}px;
  @media (min-width: 1024px) {
    width: ${({ $wideGutter }) =>
      $wideGutter ? trackCardWidthCss(200, 4.3, 24) : CARD_WIDTH_DESKTOP_PX + 'px'};
  }
`;

// THE-1391 shepherd QA round 2 tried moving the hover/active lift off
// `Card` onto a wrapper around just the tile+text column, reasoning that
// `transform` (any value but `none`) forces its element to become a
// stacking context regardless of `position`/`z-index`, and once `Card`
// itself transformed, `ConfettiLayer`'s `z-index: 26` stopped comparing
// against the raised header's `24` at their real common ancestor. That
// traded one bug for another (round 3 shepherd QA): the wrapper moved the
// tile+text, but the white box itself — background, shadow, radius,
// padding, all declared on `Card` and pinned there by
// ComingUpStripStyleContract.test.ts — stayed put, so only the card's
// CONTENTS lifted/shrank while its visible edges never moved.
//
// Round 3 fix: put the transform back on `Card` itself, exactly like every
// other card (so the whole box moves, not just its insides), and instead
// rescue the confetti from the stacking context `Card` becomes. A
// positioned element with a non-`auto` `z-index` is compared against its
// stacking siblings as ONE unit — everything painted inside it, trapped
// descendants included — so giving `Card` its own `z-index: 26` for the
// one card that hosts confetti (`$confettiCard`, below) keeps that whole
// unit, confetti included, above the header's `24` even though `Card` now
// owns a stacking context of its own. Every other card gets no `z-index`
// rule at all and is unaffected.
const Card = styled(Link)<{
  $wideGutter?: boolean;
  $confettiCard?: boolean;
}>`
  flex: 0 0 auto;
  position: relative;
  scroll-snap-align: start;
  /* Explicit, not inherited from Bootstrap's reboot. The app imports
     bootstrap.css in src/index.js, which sets box-sizing: border-box
     globally — but Storybook's preview.ts imports only src/index.css, so
     the same card measures 210px in the app and 234px (210 + 12px padding
     each side) in Storybook. Declaring it here makes the spec'd width the
     measured width in both, and stops a third-party global from owning
     this PR's one geometry number. */
  box-sizing: border-box;
  ${CARD_WIDTH_CSS}
  min-height: 84px;
  border-radius: 16px;
  border: none;
  background: #ffffff;
  box-shadow: ${CARD_SHADOW};
  padding: 10px 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: left;
  gap: 10px;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.sans};
  color: hsl(var(--foreground));
  text-decoration: none;
  transition: transform 150ms ease, box-shadow 150ms ease;
  &:hover {
    box-shadow: ${CARD_SHADOW_HOVER};
    color: hsl(var(--foreground));
    transform: translateY(-1px);
  }
  &:active {
    transform: scale(0.98);
  }
  ${({ $confettiCard }) =>
    // Plain string, not a nested `css` template — a literal backtick here
    // would end ComingUpStripStyleContract.test.ts's `block('Card')` read
    // early, the same constraint the old `CARD_LIFT_SELECTOR` concatenation
    // (and Scroller's own `PAGE_GUTTER_DESKTOP` one) worked around.
    //
    // THE-1391 shepherd QA round 4 (2026-11-03): this rescue exists to clear
    // the raised desktop header's z-index 24 (see `ConfettiLayer` below),
    // which only raises itself >=1024px. Below that the header never lifts
    // off static and today's rule was unconditional, so hovering/pressing
    // the today card on a phone bumped it to z-index 26 — ABOVE the mobile
    // top bar (24) and bottom tab bar (25), per `MobileTop`/the tab bar's
    // own z-index — and `:hover` sticks after a tap on touch devices, so the
    // card stayed drawn over both bars until the user tapped elsewhere.
    // Kate's ruling is that phones stay exactly as they were before this
    // ticket, so the rescue is now gated to the SAME `>=1024px` breakpoint
    // `ConfettiLayer`'s own z-index bump and the header's `$raised`/`$pinned`
    // use — this card gets no z-index rule at all below it, same as before.
    $confettiCard
      ? '@media (min-width: 1024px) { &:hover { z-index: 26; } &:active { z-index: 26; } }'
      : ''}
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px hsl(var(--ring) / 0.3), ${CARD_SHADOW};
  }
`;

// Placeholder card for the strip's unresolved state (THE-1134). Sized off
// the SAME width/min-height constants as the real `Card` so the shimmer
// and the hydrated content measure identically — swapping one for the
// other is a content change, not a layout change.
const ShimmerCard = styled.div<{ $wideGutter?: boolean }>`
  flex: 0 0 auto;
  box-sizing: border-box;
  ${CARD_WIDTH_CSS}
  min-height: 84px;
  border-radius: 16px;
  ${shimmerFill}
`;

const SHIMMER_CARD_COUNT = 3;

// Hard cap on the "unresolved" shimmer (THE-1134 review follow-up): only
// `hydrated` and `prefsReady`/`prefsErrored` end it, and a listener that
// stalls WITHOUT ever erroring (gotchas-web-frontend: mobile Safari can sit
// on a Firestore listener for ~15-20s) would otherwise keep an animated
// `aria-busy` band up indefinitely. Set above that documented ceiling, not
// at LandingPage's unrelated `AUTH_SETTLE_SHIMMER_MAX_MS` (8000) — collapsing
// before a slow-but-genuinely-still-arriving snapshot would hide the band
// and then pop it back open when the data lands, which is the exact reflow
// this component exists to prevent.
const COMING_UP_UNRESOLVED_MAX_MS = 20_000;

// ─── Date tile ──────────────────────────────────────────────────────
// 54x60, spec'd once and the SAME at every breakpoint — the type inside
// it is fixed, so there is nothing for a desktop step to buy. Fixed via
// width/height rather than aspect-ratio on purpose: jsdom cannot see
// `aspect-ratio` at all, and these two numbers are the thing the spec
// pins.
//
// Colours are the SAME three day-type tokens the Calendar tab's month grid
// paints its day fills with, read through the same `dayFill` roles:
//
//   today    → `darkChocolate` + white      (11.50:1)
//   people   → `deepClay` + white           (6.97:1)
//   holiday  → `mocha` + `mochaText`        (7.48:1)
//
// THE-793 item 1: before this, EVERY non-today tile was `linen` + `mochaText`,
// so a holiday and a friend's birthday were the same colour on the strip while
// the calendar grid drew them in two different ones. QA read the strip as
// broken, correctly — the strip was the surface that had invented a
// colour-blind treatment, not the grid.
//
// This MOVES the today tile from `deepClay` to `darkChocolate`, a deliberate
// deviation from THE-746 board answer #7 (which specified the deep-clay tile
// as the replacement for the old blush gradient). Answer #7 picked deep clay
// when it was the only accent on the strip; now that `deepClay` has to carry
// "one of your people" on both surfaces, leaving today on it would make a
// friend's birthday today and a friend's birthday next week identical AND
// collide with the grid's legend. `darkChocolate` is the token that already
// means "today" one tab over, so the strip gains the distinction rather than
// losing it. Flagged for the board on THE-793.
const TILE_WIDTH_PX = 54;
const TILE_HEIGHT_PX = 60;

/** Which of the three calendar day-type roles a tile paints. Mirrors
 *  `MonthGrid.DayType` minus 'plain' — a card on the strip always has an
 *  event, so there is no empty-day case here. */
export type TileKind = 'today' | 'people' | 'holiday';

const DateTile = styled.div<{ $kind: TileKind }>`
  flex: 0 0 auto;
  width: ${TILE_WIDTH_PX}px;
  height: ${TILE_HEIGHT_PX}px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  background: ${({ theme, $kind }) =>
    $kind === 'today'
      ? theme.color.darkChocolate
      : $kind === 'holiday'
        ? theme.color.mocha
        : theme.color.deepClay};
  color: ${({ theme, $kind }) =>
    $kind === 'holiday' ? theme.color.mochaText : '#ffffff'};
`;

const TileMonth = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1;
`;

const TileDay = styled.span`
  font-size: 22px;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
`;

// The text column to the right of the tile.
const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
`;

const TopRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: hsl(var(--foreground));
  max-width: 100%;
  > span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Subtext = styled.div`
  font-size: 12px;
  color: hsl(var(--muted-foreground));
`;

// Was the literal #c44b3a. Reads from `deepClay` now — same role (the
// warm accent on a white card), but a token, and 6.97:1 instead of
// unmeasured.
const When = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.deepClay};
  letter-spacing: 0.01em;
`;

// ─── Day-of confetti ────────────────────────────────────────────────
// One-shot burst from the top of the today-card on mount. Ends at
// opacity 0 (fill-mode forwards) so the settled card is clean; under
// prefers-reduced-motion nothing animates and nothing shows. Particle
// geometry is a pure function of index (NO Math.random) so renders are
// deterministic — required for Happo-stable stories and jsdom tests.
//
// Timing + shape (Kate 2026-07-10, two rounds of "I didn't see it"):
// the base delay holds the burst until the page has settled — firing
// during initial paint got lost in the load shuffle — and the pieces
// now launch from ACROSS the card's whole top edge (not one point by
// the emoji) and rise well above the card, so the burst reads at a
// glance. Still one-shot (no looping; no gamification).
const CONFETTI_BASE_DELAY_S = 0.5;
const CONFETTI_DURATION_S = 1.8;

const confettiPop = keyframes`
  0%   { opacity: 0; transform: translate(0, 0) rotate(0) scale(0.5); }
  8%   { opacity: 1; }
  75%  { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--tx), var(--ty)) rotate(var(--rot)) scale(1); }
`;

const ConfettiLayer = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 0;
  pointer-events: none;
  z-index: 1;
  /* THE-1391 shepherd QA: HubNavigationView's Desktop bar raises itself to
     \`position: relative; z-index: 24\` on the People page (\`$raised\`) to win
     hit-testing against this strip's reclaimed headroom — see Scroller's own
     comment. That stacking boost also starts painting the now-opaque header
     OVER this layer once a piece rises into its box, which the ticket's bar
     rules out ("the day-of birthday confetti should still play [in full]").
     \`pointer-events: none\` above already keeps this layer out of the click
     fix entirely, so raising it back above the header costs nothing there —
     it only has to win paint order, not hit-testing.
     >=1024px ONLY, matching exactly where \`$raised\`/\`$pinned\` apply (both
     live inside \`Desktop\`'s own \`@media (min-width: 1024px)\` block): below
     that width the header never lifts off static, z-index 1 already paints
     above it today (a static box has no explicit stack level), and bumping
     this unconditionally would change that untouched, byte-identical mobile
     stacking for no reason. 26 clears both \`$raised\`/\`$pinned\`'s 24 and the
     mobile-only bottom tab bar's 25 (itself display: none here), so this
     never ties with either. */
  @media (min-width: 1024px) {
    z-index: 26;
  }
`;

const ConfettiPiece = styled.i`
  position: absolute;
  top: 0;
  display: block;
  width: 9px;
  height: 9px;
  border-radius: 1px;
  opacity: 0;
  animation: ${confettiPop} ${CONFETTI_DURATION_S}s ease-out forwards;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

// Brand-family palette: primary-glow gold, clay, liked-pink, accent-blue,
// and a sage — all drawn from theme/index.css values.
const CONFETTI_COLORS = [
  'hsl(41 96% 58%)',
  '#B56B58',
  'hsl(350 70% 65%)',
  'hsl(189 39% 62%)',
  'hsl(140 35% 55%)',
  '#c44b3a',
];
const CONFETTI_COUNT = 24;

/** Deterministic burst: launch position spreads across the card's top
 *  edge, travel/rotation/delay derive from the particle index — never
 *  from Math.random. Pieces fly UP and fan outward (a fountain from
 *  the card), rising up to ~90px above it. */
const DayOfConfetti: React.FC = () => (
  <ConfettiLayer aria-hidden="true" data-testid="dayof-confetti">
    {Array.from({ length: CONFETTI_COUNT }, (_, i) => {
      // Spread origins across the card width: 8% → 92%.
      const leftPct = 8 + ((i * 37) % 85);
      // Fan: mostly-up angles between -130° and -50°.
      const angleDeg = -130 + ((i * 29) % 80);
      const angle = (angleDeg * Math.PI) / 180;
      const dist = 48 + ((i * 53) % 45);
      const tx = Math.cos(angle) * dist;
      const ty = Math.sin(angle) * dist;
      const rot = ((i * 97) % 260) - 130;
      return (
        <ConfettiPiece
          key={i}
          style={
            {
              left: `${leftPct}%`,
              '--tx': `${tx.toFixed(1)}px`,
              '--ty': `${ty.toFixed(1)}px`,
              '--rot': `${rot}deg`,
              background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              animationDelay: `${(CONFETTI_BASE_DELAY_S + (i % 8) * 0.06).toFixed(2)}s`,
              borderRadius: i % 3 === 0 ? '50%' : '1px',
            } as React.CSSProperties
          }
        />
      );
    })}
  </ConfettiLayer>
);

/** Whole-day diff between the event's CALENDAR DATE and the viewer's
 *  LOCAL calendar date. Exported for tests + useAlerts.
 *
 *  Two different calendars on purpose (2026-07-10 "Ashley at 7:23pm"
 *  bug): the event side reads UTC fields because the BE encodes the
 *  date as midnight UTC — it's a date, not a moment. The `now` side
 *  reads LOCAL fields because "today" means today where the viewer is
 *  standing; the device clock already knows. Comparing UTC-vs-UTC
 *  flipped every birthday to "past" at 5-9pm local for US users.
 *  When the BE provides `eventDateIso` (thea-serverless #72), prefer
 *  it — the explicit contract; the UTC decode is the fallback for
 *  docs materialized before #72. */
export function daysFromToday(
  nextEventAt: Date,
  now: Date,
  eventDateIso?: string,
): number {
  let eventCal: number;
  const isoMatch = eventDateIso && /^(\d{4})-(\d{2})-(\d{2})$/.exec(eventDateIso);
  if (isoMatch) {
    eventCal = Date.UTC(
      Number(isoMatch[1]),
      Number(isoMatch[2]) - 1,
      Number(isoMatch[3]),
    );
  } else {
    eventCal = Date.UTC(
      nextEventAt.getUTCFullYear(),
      nextEventAt.getUTCMonth(),
      nextEventAt.getUTCDate(),
    );
  }
  // LOCAL calendar date of the viewer's now, re-encoded onto the same
  // (UTC) number line so the subtraction is pure calendar math.
  const todayCal = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((eventCal - todayCal) / MS_PER_DAY);
}

const MONTH_ABBREV = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Month abbreviation + day number for the date tile.
 *
 *  Reads the event's date on exactly the same basis as `daysFromToday`:
 *  `eventDateIso` when the BE provided it, else the timestamp's **UTC**
 *  fields — because the BE encodes the date as midnight UTC and it is a
 *  date, not a moment. Using local fields here (or
 *  `toLocaleDateString()` without `timeZone: 'UTC'`) would print the
 *  PREVIOUS day for every viewer west of UTC after ~5pm — the same
 *  "Ashley at 7:23pm" class of bug daysFromToday exists to avoid, and it
 *  would show "Jul 9" on a card reading "Today".
 *
 *  Exported for tests. */
export function eventTileDate(
  nextEventAt: Date,
  eventDateIso?: string,
): { month: string; day: number } {
  const m = eventDateIso && /^(\d{4})-(\d{2})-(\d{2})$/.exec(eventDateIso);
  if (m) {
    const monthIdx = Number(m[2]) - 1;
    const day = Number(m[3]);
    // A well-formed-but-impossible iso ("2026-13-40") falls through to
    // the timestamp rather than rendering `undefined`.
    if (monthIdx >= 0 && monthIdx <= 11 && day >= 1 && day <= 31) {
      return { month: MONTH_ABBREV[monthIdx], day };
    }
  }
  return {
    month: MONTH_ABBREV[nextEventAt.getUTCMonth()],
    day: nextEventAt.getUTCDate(),
  };
}

/**
 * The `/calendar?m=&d=&e=` deep link for one Coming Up entry (PR 2.5).
 *
 * Both halves read `nextEventAt`'s **UTC** fields — deliberately NOT
 * `eventDateIso`, even though the date tile above prefers it. The link only
 * works if it names the ids the event rail actually renders, and
 * CalendarPage builds those from `nextEventAt` (`isoUtcDate(when)` for the
 * day key, `when.getUTCFullYear()` for the id). Reading the other field
 * here would silently produce a link that resolves to nothing the day the
 * two ever disagree. Same reason the eventType narrowing below mirrors
 * CalendarPage's exactly rather than being "cleaned up": the id has to
 * collide with theirs, not merely look like it.
 *
 * Exported for tests.
 */
export function comingUpDeepLink(
  recipientId: string,
  eventType: string,
  nextEventAt: Date,
): string {
  const y = nextEventAt.getUTCFullYear();
  const m = String(nextEventAt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(nextEventAt.getUTCDate()).padStart(2, '0');
  const personType: PersonEventType =
    eventType === 'anniversary' ? 'anniversary' : 'birthday';
  return buildCalendarDeepLink(
    `${y}-${m}-${d}`,
    personCalendarEventId(recipientId, personType, y),
  );
}

/** Countdown wording for PERSON cards (birthday / anniversary) only —
 *  plan item 15 locked this ladder, including the "Next week" case for days
 *  7-13. Holiday cards used to borrow this same ladder ("holidays get no
 *  countdown ladder of their own"); THE-1190 gave them their own rule
 *  instead (`calendar/holidayCountdown.holidayCountdownLabel`), shared with
 *  the Gift guides hub, so this ladder now only needs to stay right for
 *  people. */
export function formatRelativeDays(days: number): string {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days < 7) return `In ${days} days`;
  if (days < 14) return 'Next week';
  if (days < 30) return `In ${days} days`;
  if (days < 60) return `In ${Math.round(days / 7)} weeks`;
  return `In ${Math.round(days / 30)} months`;
}

/** A stable identity for one merged entry — the SAME string a holiday card's
 *  own React key (`holiday-${eventType}-${dayIso}`) or a person card's own
 *  recipient+type pair resolves to, used only to detect when a DIFFERENT
 *  entry has become entry #0, never rendered. Exported for tests. */
export function leadEntryKey(entry: ComingUpEntry): string {
  return entry.kind === 'holiday'
    ? `holiday-${entry.holiday.eventType}-${entry.dayIso}`
    : `person-${entry.event.recipientId}-${entry.event.eventType}`;
}

function eventLabel(eventType: string): string {
  if (eventType === 'birthday') return 'Birthday';
  if (eventType === 'anniversary') return 'Anniversary';
  return eventType;
}

/** The holiday card's second line — locked by the spec to this one word
 *  (THE-746 item 7), where a person card carries "Birthday" / "Anniversary".
 *  Deliberately NOT the holiday's own label (that is the first line) and
 *  deliberately not per-holiday copy: copy is final. */
const HOLIDAY_SUBTEXT = 'Holiday';

interface ComingUpStripProps {
  /** When true, hides the strip if the materialized list is empty. The
   *  parent (MyPeoplePage) keeps the rest of the page intact. */
  hideWhenEmpty?: boolean;
  /** Test/story injection: bypass the Firestore hook with fixed events.
   *  Production callers omit this and stream from materializedComingUp. */
  events?: ComingUpEvent[];
  /** Test/story injection: fixed reference time for deterministic
   *  relative labels. Defaults to the real clock. */
  now?: Date;
  /** Day-of confetti burst on the "Today" card. Stories pass false so
   *  Happo snapshots stay animation-free. */
  confetti?: boolean;
  /**
   * The user's SAVED hidden-holiday list (`hiddenHolidays` on `theaWebUser`,
   * THE-761). Empty — and absent — mean ALL HOLIDAYS ON.
   *
   * OVERRIDE ONLY as of PR 3.3 (THE-764). Production renders pass nothing and
   * the strip reads the saved doc itself via `useHiddenHolidays()` — the same
   * shape `events` has (`events ?? hook.upcoming`), and the same shape the
   * calendar half uses, so the two surfaces read their list the same way
   * instead of one of them depending on where it happens to be mounted.
   *
   * Deliberately NOT prop-drilled from `LandingPage`: the marketing page has
   * nothing to do with holidays, and routing a Firestore read through it put
   * `useDb` into four of its suites for a feature that renders nothing.
   *
   * Typed `unknown`, matching `comingUpEntries`: the value originates in a
   * Firestore document, so the wrong SHAPE must fail safe rather than throw
   * inside a render. See `holidayPrefs/hiddenHolidays.ts`.
   */
  hiddenHolidays?: unknown;
  /**
   * Has the saved list ARRIVED? Override only; production uses
   * `useHiddenHolidays().ready`.
   *
   * `false` means NOTHING renders: the hook's pre-snapshot `[]` is
   * indistinguishable from a genuine "she has hidden nothing", and guessing
   * all-on there flashes exactly the holidays she went to the Holidays page to
   * hide. Defaults to `true` when a caller supplies `hiddenHolidays`
   * explicitly — such a caller is stating the list, so its arrival is not in
   * question.
   */
  holidayPrefsReady?: boolean;
  /**
   * Override the Phase 3 release gate. 🔒 PRODUCTION CALLERS MUST NOT PASS
   * THIS. It defaults to `HOLIDAYS_RELEASED_IN_COMING_UP`, which is `true` as
   * of THE-764 — the shipped build merges real holiday cards in. Tests and
   * stories pass an explicit override to pin their scenario independently of
   * that default; that is the same injection seam `events` and `now` already
   * are, not a temporary enablement of the feature.
   * `comingUpEntries.releaseGate.test.ts` asserts `LandingPage.tsx` never
   * supplies it.
   */
  holidaysReleased?: boolean;
  /**
   * THE-1161 (rides the Gift Guide hub gate; no preview switch of its own) — defaults to
   * `false` so every existing story/test and real visitor keeps rendering
   * today's production layout when the Gift Guide hub gate is off.
   * `LandingPage.tsx`/`CalendarPage.tsx` resolve the flag once and pass it
   * down; this component stays Firebase/Router-free, matching every other
   * gate in this family (giftGuideHubFlag.ts, relatedGuidesFlag.ts).
   */
  wideGutterEnabled?: boolean;
  /**
   * The hub's occasion collections, keyed by their kebab-case collection key,
   * each holding the guide slugs it collects — same shape `CalendarPage`
   * builds from `useHubOccasionMapping()` and feeds `eventDestination()`
   * (THE-1403). A card's tap must land exactly where the same event lands on
   * the Calendar, so the two surfaces resolve it from the one function fed
   * the one doc; omitted (every real user while the hub gate is off or still
   * loading) means exactly today's routing, same as `eventDestination`'s own
   * default. `LandingPage.tsx` resolves this once and passes it down —
   * this component stays Firebase/Router-free, matching every other gate in
   * this family.
   */
  hubCollections?: ReadonlyMap<string, readonly string[]>;
}

export const ComingUpStrip: React.FC<ComingUpStripProps> = ({
  hideWhenEmpty = true,
  events,
  now: nowOverride,
  confetti = true,
  hiddenHolidays,
  wideGutterEnabled = false,
  holidayPrefsReady,
  holidaysReleased,
  hubCollections,
}) => {
  const hubLink = useGiftGuideHubLink();
  const hook = useMaterializedComingUp();
  const location = useLocation();
  const scrollMemoryKey = `${hook.uid ?? ''}|${location.key}`;
  const upcoming = events ?? hook.upcoming;
  const hydrated = events ? true : hook.hydrated;

  // The saved hidden-holiday list (THE-764). Props win when supplied, exactly
  // as `events` wins over the materialized hook above; production supplies
  // neither and reads the user's own doc.
  //
  // A caller that states `hiddenHolidays` is asking a question about the LIST,
  // not about its arrival, so `ready` defaults true for it — that is what lets
  // PR 2.4's existing call sites read unchanged.
  const holidayPrefs = useHiddenHolidays();
  const hiddenList = hiddenHolidays !== undefined ? hiddenHolidays : holidayPrefs.hiddenHolidays;
  const prefsReady =
    hiddenHolidays !== undefined
      ? holidayPrefsReady ?? true
      : holidayPrefsReady ?? holidayPrefs.ready;
  // A caller that states `hiddenHolidays` is also stating the answer, so
  // there is no subscription underneath it that can fail — it cannot error.
  // Only the hook path can report THE-1134's permanent-read-failure case.
  const prefsErrored = hiddenHolidays !== undefined ? false : holidayPrefs.error;

  const people = useMemo<ComingUpPersonEntry[]>(() => {
    if (!hydrated) return [];
    const now = nowOverride ?? new Date();
    const out: ComingUpPersonEntry[] = [];
    upcoming.forEach((e) => {
      // Firestore Timestamps from snapshot.data() have a `.toDate()`
      // method; defensive guard handles serialized cases too.
      const ts = e.nextEventAt as unknown as {
        toDate?: () => Date;
        seconds?: number;
      };
      let when: Date;
      if (typeof ts?.toDate === 'function') {
        when = ts.toDate();
      } else if (typeof ts?.seconds === 'number') {
        when = new Date(ts.seconds * 1000);
      } else {
        when = new Date(ts as unknown as string);
      }
      if (Number.isNaN(when.getTime())) return;
      // LOCAL-day window: the BE retains an event through the UTC day
      // after its date so every timezone gets its full local day-of;
      // the FE is the layer that decides visibility. days < 0 = the
      // viewer's own calendar has moved past the event → hide.
      const days = daysFromToday(when, now, e.eventDateIso);
      if (days < 0) return;
      if (days > LOOK_AHEAD_DAYS) return;
      out.push({
        kind: 'person',
        event: e,
        when,
        dayIso: eventDayIso(when, e.eventDateIso),
        days,
      });
    });
    return out;
  }, [hydrated, upcoming, nowOverride]);

  // The holiday arm. `HOLIDAYS_RELEASED_IN_COMING_UP` opened as of THE-764;
  // returns [] only if a caller overrides the gate shut, or before
  // `prefsReady` (see comingUpEntries.HOLIDAYS_RELEASED_IN_COMING_UP).
  //
  // Not gated on `hydrated`: holidays are client-side date math and owe
  // nothing to the materialized doc. A user with no recipients still gets
  // her strip on first paint rather than after a Firestore round trip.
  const holidays = useMemo(
    () =>
      holidayEntriesForComingUp({
        now: nowOverride ?? new Date(),
        lookAheadDays: LOOK_AHEAD_DAYS,
        hiddenHolidays: hiddenList,
        ready: prefsReady,
        released: holidaysReleased,
      }),
    [nowOverride, hiddenList, prefsReady, holidaysReleased],
  );

  // ONE chronological list. Sorted in comingUpEntries, not here: the holiday
  // table is in mockup order, which is not a date sort.
  const visible = useMemo(
    () => mergeComingUpEntries(people, holidays),
    [people, holidays],
  );

  // THE-1161 shepherd QA: the person arm resolves a beat after the holiday
  // arm, and when it does, an earlier-dated event (a recipient's own
  // birthday) can land AHEAD of cards already rendered — "whatever's
  // ready" render strategy below, by design. Measured in a real browser:
  // neither `overflow-anchor: none` nor `scroll-snap-type: x proximity`
  // stops Chromium from re-snapping to keep the PREVIOUSLY-first card's
  // on-screen position fixed, which pushes the newly-prepended card mostly
  // out of view instead of showing it. The deterministic fix is to drive
  // scrollLeft ourselves: whenever entry #0's own identity changes (a
  // different, earlier entry has taken the lead), snap the strip back to
  // its start so the real first card is the one actually on screen. A
  // mount with nothing yet to show, or a later arm settling without
  // reordering entry #0, leaves the ref untouched.
  //
  // DESKTOP ONLY (THE-1161 shepherd re-QA round 5 correction). The bug above
  // was measured in a real desktop browser's scroll-anchoring behaviour —
  // nothing here establishes it reproduces on mobile, and this effect ran
  // unconditionally before this fix. Left ungated, a phone visitor who had
  // already scrolled the strip right before the person arm settled would
  // get silently snapped back to its start, mid-browse, for a desktop-only
  // rendering quirk she was never subject to — exactly the kind of mobile
  // side effect the ticket's "mobile unchanged" acceptance bar rules out
  // (the Scroller's own >=1024px-only bleed, just above, drew the same
  // line for the same reason). `window.matchMedia` rather than a ref to
  // `Scroller`'s own breakpoint: this is a behavioural gate, not a layout
  // one, so there is no CSS property to read it off of.
  //
  // Shepherd review (THE-1161): also gated on `wideGutterEnabled`, same
  // reasoning as the Scroller's `overflow-anchor: none` above — this fixes
  // a real bug that predates the ticket, but shipping the behavior change
  // to every off-param desktop visitor is scope creep past "no change when
  // off." Un-gate once the fix itself ships on its own.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const leadKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!wideGutterEnabled) return;
    const nextLeadKey = visible.length > 0 ? leadEntryKey(visible[0]) : undefined;
    const isDesktop =
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(min-width: 1024px)').matches;
    if (
      isDesktop &&
      nextLeadKey !== undefined &&
      nextLeadKey !== leadKeyRef.current &&
      scrollerRef.current
    ) {
      scrollerRef.current.scrollLeft = 0;
    }
    leadKeyRef.current = nextLeadKey;
  }, [visible, wideGutterEnabled]);

  // THE-1134: before the materialized doc hydrates we don't yet know
  // whether there's anything to show, so we can't pick between "render
  // cards" and "render nothing" — guessing wrong is exactly how the grid
  // below gets shoved down mid-render (cold load) or torn out from under a
  // restored scroll position (Back from a board, which remounts this
  // component same as a fresh load). Reserve the real height with shimmer
  // cards instead: hydrating into actual content is then a content swap
  // inside an already-sized box, not a reflow.
  //
  // "Resolved" needs BOTH arms that feed `visible`, not just the person
  // one: the holiday arm reports in via `prefsReady`, but only while the
  // gate is open — closed, `holidays` already came back `[]` above without
  // touching the prefs doc, so there's nothing left to wait for from that
  // arm. Gating on `hydrated` alone shipped two bugs:
  //  - An anon visitor's holiday arm is ready immediately (no saved doc to
  //    wait for — see `useHiddenHolidays`), while her person arm still has
  //    a round trip ahead. She'd sit behind this shimmer for the holiday
  //    cards master painted on the first frame.
  //  - A signed-in visitor whose person arm resolves to "nothing due" while
  //    the holiday arm is still pending would hit the KNOWN-empty collapse
  //    below immediately, then have holiday content land a beat later:
  //    SHIMMER → gone → REAL, two visible shifts where master had one.
  // Rendering whatever's ready as soon as `visible` is non-empty — even
  // with the other arm still pending — avoids both: the Scroller's height
  // doesn't change when a card is added to it later, so an early
  // holiday-only (or person-only) render is exactly as safe as the final
  // one. `events` callers (tests/stories) are hydrated synchronously so
  // never see the shimmer branch.
  //
  // Bounded against a PERMANENT failure (QA on 83f2308e): `prefsReady` alone
  // never flips true if the `theaWebUser/{uid}` read is denied — it would
  // otherwise shimmer forever for a signed-in visitor whose person arm has
  // already resolved to "nothing due". `useHiddenHolidays().error` reports
  // that failure distinctly from "first snapshot still in flight", so a
  // denied read counts as the holiday arm settling (to showing nothing,
  // which is already the fail-closed behaviour `prefsReady: false` gives
  // `holidayEntriesForComingUp` below) rather than as still-pending.
  const holidaysGateOpen = holidaysReleased ?? HOLIDAYS_RELEASED_IN_COMING_UP;
  const holidayArmSettled = !holidaysGateOpen || prefsReady || prefsErrored;
  // `isStaticRender` excludes `renderRoute.tsx`'s prerender pass (THE-1134
  // review follow-up): `renderToStaticMarkup` never runs an effect, so
  // neither arm's listener ever attaches there, and
  // `hydrated`/`holidayArmSettled` would stay at their
  // pre-subscription `false` forever — shipping an animated
  // `aria-busy` shimmer as permanent markup for every crawler, Reader-mode
  // and pre-JS first paint. That render can never get an answer, so it
  // takes the KNOWN-empty collapse below instead — the same output master
  // shipped before this component existed. See `staticRenderContext.ts` for
  // why this is a context rather than a `typeof window` check.
  const isStaticRender = useIsStaticRender();
  const unresolved = !isStaticRender && (!hydrated || !holidayArmSettled);
  const [unresolvedTimedOut, setUnresolvedTimedOut] = useState(false);
  useEffect(() => {
    if (!unresolved) {
      setUnresolvedTimedOut(false);
      return;
    }
    const t = setTimeout(
      () => setUnresolvedTimedOut(true),
      COMING_UP_UNRESOLVED_MAX_MS,
    );
    return () => clearTimeout(t);
  }, [unresolved]);

  // Restore the remembered scroll offset on a Back to this same history
  // entry (THE-1403). The target is decided once, at mount; it is then
  // re-applied on EVERY commit until both arms have settled, because after a
  // Back the first render with cards is usually holidays only — the person
  // arm lands a round trip later — and a one-shot restore against that short
  // track gets clamped. While pending, `onScroll` below doesn't record (the
  // clamp's own scroll event would overwrite the real offset), and this
  // effect claims the current lead key so the desktop reset-to-0 above
  // doesn't undo the restore when person cards are prepended. A layout
  // effect, so the strip never paints one frame at the first card first.
  // A touch, pointer or wheel on the strip cancels it: the shopper's own
  // scroll beats a late restore.
  const [restoreTarget] = useState(() => rememberedScrollLeft.get(scrollMemoryKey));
  const restorePendingRef = useRef(restoreTarget !== undefined);
  const cancelRestore = () => { restorePendingRef.current = false; };
  useBeforePaint(() => {
    if (!restorePendingRef.current || restoreTarget === undefined) return;
    const scroller = scrollerRef.current;
    if (!scroller || visible.length === 0) return;
    scroller.scrollLeft = restoreTarget;
    leadKeyRef.current = leadEntryKey(visible[0]);
    if (!unresolved || unresolvedTimedOut) restorePendingRef.current = false;
  }, [visible, unresolved, unresolvedTimedOut, restoreTarget]);

  if (visible.length === 0) {
    if (unresolved && !unresolvedTimedOut) {
      return (
        <Band aria-label="Coming up" data-testid="coming-up-band" aria-busy="true">
          <Inner data-testid="coming-up-banner" $wideGutter={wideGutterEnabled}>
            <Title>Coming up</Title>
            <Scroller aria-hidden="true" $wideGutter={wideGutterEnabled}>
              {Array.from({ length: SHIMMER_CARD_COUNT }, (_, i) => (
                <ShimmerCard key={i} data-testid="coming-up-shimmer-card" $wideGutter={wideGutterEnabled} />
              ))}
            </Scroller>
          </Inner>
        </Band>
      );
    }
    // Every arm reported in and all of them are empty: a KNOWN empty, not
    // an unresolved one, so the strip collapses rather than keeping the
    // shimmer's reserved gap forever (THE-1134 ground rule).
    if (hideWhenEmpty) return null;
  }

  return (
    <Band aria-label="Coming up" data-testid="coming-up-band">
      <Inner data-testid="coming-up-banner" $wideGutter={wideGutterEnabled}>
        <Title>Coming up</Title>
        <Scroller
          ref={scrollerRef}
          data-testid="coming-up-scroller"
          $wideGutter={wideGutterEnabled}
          onScroll={(e) => {
            if (restorePendingRef.current) return;
            rememberedScrollLeft.set(scrollMemoryKey, e.currentTarget.scrollLeft);
          }}
          onPointerDown={cancelRestore}
          onTouchStart={cancelRestore}
          onWheel={cancelRestore}
        >
        {visible.map((entry, i) => {
          const { days, dayIso } = entry;
          const isToday = days === 0;

          if (entry.kind === 'holiday') {
            const h = entry.holiday;
            const year = Number(dayIso.slice(0, 4));
            // Midnight UTC, matching how the BE encodes a person event's
            // date, so BOTH arms take eventTileDate's `eventDateIso` branch
            // and one tile cannot disagree with the other about what day it
            // is west of UTC.
            const tile = eventTileDate(new Date(`${dayIso}T00:00:00.000Z`), dayIso);
            // Lands exactly where the Calendar tab lands this same event
            // (THE-1403) — the board/guide/collection `eventDestination`
            // resolves, via the SAME hub-collection data the Calendar reads.
            // `null` (no gift page yet) falls back to today's exact-event
            // /calendar deep link, unchanged.
            const dest = eventDestination(
              {
                id: holidayCalendarEventId(h.eventType, year),
                date: dayIso,
                eventType: h.eventType,
                recipient: null,
                isComplete: false,
                productThumbs: [],
                guideSlug: h.guideSlug,
              },
              hubCollections,
            );
            const kind = destinationKind(dest);
            return (
              <Card
                $wideGutter={wideGutterEnabled}
                key={`holiday-${h.eventType}-${dayIso}`}
                data-day-iso={dayIso}
                // The id comes from `holidayCalendarEventId`, the same
                // function `holidayToCalendarEvent` builds the rail's id
                // with, so a fallback deep link's `?e=` cannot drift from
                // the card it targets.
                to={hubLink(dest ?? buildCalendarDeepLink(dayIso, holidayCalendarEventId(h.eventType, year)))}
                state={kind === 'guide' || kind === 'collection' || kind === 'occasion' ? PEOPLE_ENTRY : undefined}
                // The holiday's own label, alone — the calendar's holiday
                // cards announce the type label alone too (EventCard), and
                // there is no person to possessive-case.
                aria-label={h.label}
                data-today={isToday || undefined}
                // 🔒 PRIVACY (THE-746 plan rev 4, board correction). The
                // GENERIC constant, never `h.eventType`: a tap count is
                // allowed, the holiday's identity is not. `days_until` is
                // OMITTED — GA4 stamps every hit with a time, so
                // days_until + hit time reconstructs the date, and a
                // holiday's date names the holiday. `destination` stays a
                // coarse kind — 'board' | 'guide' | 'collection' |
                // 'occasion' | 'calendar' — for the same reason (THE-1403).
                onClick={() =>
                  gaCalendarEventTap({
                    surface: 'coming_up_strip',
                    event_type: GA_HOLIDAY_EVENT_TYPE,
                    destination: kind,
                  })
                }
              >
                {/* No confetti, even on the day-of. The burst celebrates a
                    person you track (Kate, 2026-07-10); fired for a holiday
                    it would go off on every user's strip on the same date,
                    which is a different product decision and not in this
                    PR's spec. The deep-clay today tile still marks the day,
                    so the day-of treatment is not lost. */}
                <DateTile
                  $kind={isToday ? 'today' : 'holiday'}
                  aria-hidden="true"
                  data-testid="coming-up-date-tile"
                  data-tile-kind={isToday ? 'today' : 'holiday'}
                  data-today-tile={isToday || undefined}
                >
                  <TileMonth>{tile.month}</TileMonth>
                  <TileDay>{tile.day}</TileDay>
                </DateTile>
                <CardBody>
                  {/* The holiday's own glyph, then the label in ONE span so
                      TopRow's `> span:last-child` ellipsis rule lands on the
                      label — "Teacher Appreciation Day" has to truncate
                      inside a 210px card.

                      THE-793 item 2: this slot used to be omitted entirely,
                      on the reasoning that the person card's 🎁 fallback
                      would read as a gift for the holiday. That reasoning
                      was right about 🎁 and wrong about the slot — Halloween
                      rendering with no emoji at all is what QA filed. The
                      glyph is now the holiday's own (🎃), carried on the
                      entry by `comingUpEntries` so this file still reaches
                      the holiday table only through the gate, and 🎁 is gone
                      from the person card too. Decorative: the card's
                      accessible name is `h.label`. */}
                  <TopRow>
                    <span aria-hidden="true">{entry.emoji}</span>
                    <span>{h.label}</span>
                  </TopRow>
                  <Subtext>{HOLIDAY_SUBTEXT}</Subtext>
                  <When>{holidayCountdownLabel(days)}</When>
                </CardBody>
              </Card>
            );
          }

          const e = entry.event;
          const when = entry.when;
          const tile = eventTileDate(when, e.eventDateIso);
          // Only the card that actually renders <DayOfConfetti /> needs the
          // z-index rescue (see Card's own comment); every other card lifts
          // itself exactly as before, with no z-index rule at all.
          const showConfetti = isToday && confetti;
          // Lands on the recipient's board — BoardRoute decides quizzed vs
          // no-quiz vs a removed/unresolvable recipient from there, exactly
          // as it already does for a Calendar tap on this same event
          // (THE-1403). A materialized entry always carries a real
          // recipientId, so this never falls through to `null`; kept backed
          // by the calendar fallback below anyway, so this card can never
          // drift from `eventDestination`'s own contract.
          const dest = eventDestination(
            {
              id: `${e.recipientId}_${e.eventType}`,
              date: dayIso,
              eventType: e.eventType,
              recipient: { id: e.recipientId, name: e.recipientName, emoji: e.recipientEmoji ?? '' },
              isComplete: false,
              productThumbs: [],
            },
            hubCollections,
          );
          const kind = destinationKind(dest);
          const cardContent = (
            <>
              {/* Decorative: the card's accessible name already says
                  whose event this is and the countdown line says when,
                  so the tile is aria-hidden rather than lengthening the
                  announced name with "Jul 10". */}
              <DateTile
                $kind={isToday ? 'today' : 'people'}
                aria-hidden="true"
                data-testid="coming-up-date-tile"
                data-tile-kind={isToday ? 'today' : 'people'}
                data-today-tile={isToday || undefined}
              >
                <TileMonth>{tile.month}</TileMonth>
                <TileDay>{tile.day}</TileDay>
              </DateTile>
              <CardBody>
                <TopRow>
                  {/* THE-793 item 2: was `e.recipientEmoji || '🎁'`. The
                      materialized doc carries `recipientEmoji` only once the
                      BE has resolved one, so every friend without a stored
                      glyph got a present — on a BIRTHDAY card, next to a
                      Halloween card that also wanted to be a present. The
                      shared chain (explicit → relationship default → ✨, and
                      🙋 for a self board) is what the People grid, the board
                      header and the calendar's own chips already use, so the
                      same person now shows the same glyph on all four. */}
                  <span aria-hidden="true">
                    {resolveRecipientEmoji(e.recipientEmoji, undefined, e.isMe)}
                  </span>
                  {/* Self boards read second person — "Me / birthday" on a
                      card is the board name leaking; "You" is the viewer. */}
                  <span>{e.isMe ? 'You' : e.recipientName}</span>
                </TopRow>
                <Subtext>{eventLabel(e.eventType)}</Subtext>
                <When>{formatRelativeDays(days)}</When>
              </CardBody>
            </>
          );
          return (
            <Card
              $wideGutter={wideGutterEnabled}
              $confettiCard={showConfetti}
              key={`${e.recipientId}-${e.eventType}-${i}`}
              data-day-iso={dayIso}
              to={hubLink(dest ?? comingUpDeepLink(e.recipientId, e.eventType, when))}
              aria-label={
                e.isMe
                  ? `Your ${eventLabel(e.eventType)}`
                  : `${e.recipientName}'s ${eventLabel(e.eventType)}`
              }
              data-today={isToday || undefined}
              // Person cards keep the full shipped payload, `days_until`
              // included: `TheaWebComingUpEventTypeEnum` is birthday |
              // anniversary, so `e.eventType` here can only ever be one of
              // those two and carries no holiday choice. PR 2.4 did not
              // widen this call — it added a SEPARATE fire site above with
              // its own privacy contract, rather than making one call branch
              // on card type, so neither payload can drift into the other.
              // `destination` (THE-1403) is `'board'` on every real tap —
              // kept computed rather than hardcoded so a future destination
              // for person events shows up here without a second edit.
              onClick={() =>
                gaCalendarEventTap({
                  surface: 'coming_up_strip',
                  event_type: e.eventType,
                  days_until: days,
                  destination: kind,
                })
              }
            >
              {showConfetti && <DayOfConfetti />}
              {cardContent}
            </Card>
          );
        })}
        </Scroller>
      </Inner>
    </Band>
  );
};

export default ComingUpStrip;
